#!/usr/bin/env node

import * as fs from 'fs'
import { getCustomComponentLibraryConfig } from './util'
import * as path from 'path'
import { EsBuildErrorDescription } from './build_components'
import * as ts from 'typescript'


const MODEL_TYPES = ['string' , 'array', 'number', 'boolean', 'object', 'enumeration']

export type ModelType = typeof MODEL_TYPES[number]

export interface CustomComponentManifestModelField {
  name: string
  type: ModelType
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialValue: any
  inspector?: string
  enumDefinition?: string[]
  enumLabels?: {
    [key: string]: string
  }
  description?: string
  label?: string
}

export interface CustomComponentManifestEntry {
  name: string
  description?: string
  model: Array<CustomComponentManifestModelField>
  events: Array<string>
  defaultWidth?: number
  defaultHeight?: number
}

export interface CustomComponentManifest {
  customComponentSupportVersion: '2'
  components: Record<string, CustomComponentManifestEntry>
}

type LiteralType = 'string' | 'string[]' | 'number' | 'boolean' | 'object' | 'unknown' | 'stringValuedObject'

type LiteralTypeValueMap = {
  'string': string,
  'string[]': string[],
  'number': number,
  'boolean': boolean,
  'object': Record<string, unknown>
  'stringValuedObject': Record<string, string>
  'unknown': unknown
}

type LiteralTypeValue<T extends LiteralType> = LiteralTypeValueMap[T]

interface ExtractedRetoolDeclarations {
  retoolStateHooks: ts.CallExpression[]
  eventCallbacks: ts.CallExpression[]
  componentSettings: ts.CallExpression[]
}

// Changes 'FooBar' to 'Foo Bar', and AGGrid to AG Grid
function camelToSpaceTitle(str: string): string {
  // Use a regular expression to find all occurrences of capital letters
  // and replace them with a space followed by the capital letter itself.
  const result = str.replace(/([a-z0-9])([A-Z])|([A-Z])([A-Z][a-z])/g, '$1$3 $2$4').trim()
  // capitalize first letter
  return result.charAt(0).toUpperCase() + result.slice(1)
}

function findCallExpressionAtPosition(node: ts.Node, position: number): ts.CallExpression | undefined {
  if (position >= node.getStart() && position < node.getEnd()) {
    if (ts.isCallExpression(node)) {
      return node
    }

    return ts.forEachChild(node, (child) => {
      const foundChild = findCallExpressionAtPosition(child, position)
      if (foundChild) {
        return foundChild
      }
    })
  }

  return undefined
}

function getRetoolMethodCallIfExists(
  call: ts.CallHierarchyOutgoingCall,
): keyof ExtractedRetoolDeclarations | undefined {
  const to = call.to
  const name = to.name
  const containerName = to.containerName
  if (containerName === 'Retool') {
    if (name === 'useComponentSettings') {
      return 'componentSettings'
    } else if (name === 'useEventCallback') {
      return 'eventCallbacks'
    } else {
      return 'retoolStateHooks'
    }
  } else {
    return undefined
  }
}
function extractRetoolDeclarationsFromCallHierarchy(
  languageService: ts.LanguageService,
  sourceFileName: string,
  outgoingCallHierarchy: ts.CallHierarchyOutgoingCall[],
  seenFiles: Record<string, Set<number>>,
): ExtractedRetoolDeclarations {
  const results: ExtractedRetoolDeclarations = {
    retoolStateHooks: [],
    eventCallbacks: [],
    componentSettings: [],
  }

  const program = languageService.getProgram()
  if (!program) {
    throw new Error('Could not create program')
  }

  const sourceFile = program.getSourceFile(sourceFileName)
  if (!sourceFile) {
    throw new Error(`Could not find source file ${sourceFileName}`)
  }

  for (const outgoingCall of outgoingCallHierarchy) {
    const to = outgoingCall.to
    const callType = getRetoolMethodCallIfExists(outgoingCall)
    if (!callType) {
      const extracted = extractRetoolDeclarationsFromPosition(
        languageService,
        to.file,
        to.selectionSpan.start,
        seenFiles,
      )
      results.retoolStateHooks.push(...extracted.retoolStateHooks)
      results.eventCallbacks.push(...extracted.eventCallbacks)
      results.componentSettings.push(...extracted.componentSettings)
    } else {
      const fromSpans = outgoingCall.fromSpans
      if (!fromSpans.length) {
        continue
      }

      for (const fromSpan of fromSpans) {
        const foundNode = findCallExpressionAtPosition(sourceFile, fromSpan.start)
        if (!foundNode) {
          throw new Error(
            `Could not find node at position for span in file ${sourceFile.fileName} at position ${JSON.stringify(
              fromSpan,
            )} with text ${sourceFile.getText().substring(fromSpan.start, fromSpan.start + fromSpan.length)}`,
          )
        }
        results[callType].push(foundNode)
      }
    }
  }

  return results
}

function extractRetoolDeclarationsFromDefinitionsAtPosition(
  languageService: ts.LanguageService,
  file: string,
  position: number,
  // Seen files by name and position, to avoid infinite loops
  seenFiles: Record<string, Set<number>>,
): ExtractedRetoolDeclarations {
  const results: ExtractedRetoolDeclarations = {
    retoolStateHooks: [],
    eventCallbacks: [],
    componentSettings: [],
  }
  const definitions = languageService.getDefinitionAtPosition(file, position)

  if (definitions) {
    for (const definition of definitions) {
      const extracted = extractRetoolDeclarationsFromPosition(
        languageService,
        definition.fileName,
        definition.textSpan.start,
        seenFiles,
      )
      results.retoolStateHooks.push(...extracted.retoolStateHooks)
      results.eventCallbacks.push(...extracted.eventCallbacks)
      results.componentSettings.push(...extracted.componentSettings)
    }
  }

  return results
}

function extractRetoolDeclarationsFromPosition(
  languageService: ts.LanguageService,
  fileName: string,
  position: number,
  seenFiles: Record<string, Set<number>>,
): ExtractedRetoolDeclarations {
  if (seenFiles[fileName]?.has(position)) {
    return {
      retoolStateHooks: [],
      componentSettings: [],
      eventCallbacks: [],
    }
  }

  if (!seenFiles[fileName]) {
    seenFiles[fileName] = new Set()
  }
  seenFiles[fileName].add(position)

  const outgoingCallHierarchy = languageService.provideCallHierarchyOutgoingCalls(fileName, position)
  return extractRetoolDeclarationsFromCallHierarchy(languageService, fileName, outgoingCallHierarchy, seenFiles)
}

export function analyzeFile(filePath: string, tsconfigPath: string, rootDir: string): CustomComponentManifest {
  const configFile = ts.readJsonConfigFile(tsconfigPath, ts.sys.readFile)
  const config = ts.parseJsonSourceFileConfigFileContent(configFile, ts.sys, rootDir)

  const languageServiceHost: ts.LanguageServiceHost = {
    getCompilationSettings: () => config.options,
    getScriptFileNames: () => [filePath],
    getScriptVersion: () => '1',
    getScriptSnapshot: (name) => {
      const content = fs.readFileSync(name).toString()
      return ts.ScriptSnapshot.fromString(content)
    },
    getCurrentDirectory: () => process.cwd(),
    getDefaultLibFileName: (options) => ts.getDefaultLibFilePath(options),
    fileExists: ts.sys.fileExists,
    readFile: ts.sys.readFile,
  }

  const languageService = ts.createLanguageService(languageServiceHost, undefined, ts.LanguageServiceMode.Semantic)

  const program = languageService.getProgram()

  if (!program) {
    throw new Error('Could not create program')
  }

  const sourceFiles = program.getSourceFiles()

  // These are the calls, keyed by component name, that we will add to the manifest
  const foundTransitiveCallExpressions: Record<string, ExtractedRetoolDeclarations> = {}
  // resolved path
  const resolvedFilePath = path.resolve(filePath)

  // Find imports and analyze them
  for (const sourceFile of sourceFiles) {
    if (path.resolve(sourceFile.fileName) === resolvedFilePath) {
      ts.forEachChild(sourceFile, (node) => {
        // statements like `export { FooComponent } from './Foo'`
        if (ts.isExportDeclaration(node) && node.moduleSpecifier) {
          if (node.exportClause && ts.isNamedExports(node.exportClause)) {
            for (const exportSpecifier of node.exportClause.elements) {
              const exportedName = exportSpecifier.name.getText(sourceFile)
              const importedNode = exportSpecifier.propertyName ?? exportSpecifier.name

              const importedName = importedNode.getText(sourceFile)

              foundTransitiveCallExpressions[exportedName] = extractRetoolDeclarationsFromDefinitionsAtPosition(
                languageService,
                sourceFile.fileName,
                importedNode.getStart(),
                {},
              )
            }
          }
        }

        // statements like export const `FooComponent: FC = () => { return <div>FOO</div> }`
        if (ts.isVariableStatement(node) && node.modifiers?.some((mod) => mod.kind === ts.SyntaxKind.ExportKeyword)) {
          for (const declaration of node.declarationList.declarations) {
            if (ts.isIdentifier(declaration.name)) {
              const exportedName = declaration.name.getText(sourceFile)
              foundTransitiveCallExpressions[exportedName] = extractRetoolDeclarationsFromPosition(
                languageService,
                sourceFile.fileName,
                declaration.name.getStart(),
                {},
              )
            }
          }
        }

        // statements like export function BarComponent() { return <div>BAR</div> }
        if (ts.isFunctionDeclaration(node) && node.modifiers?.some((mod) => mod.kind === ts.SyntaxKind.ExportKeyword)) {
          if (!node.name) {
            throw new Error('Function declaration does not have a name')
          }
          const exportedName = node.name.getText(sourceFile) ?? ''

          foundTransitiveCallExpressions[exportedName] = extractRetoolDeclarationsFromPosition(
            languageService,
            sourceFile.fileName,
            node.name.getStart(),
            {},
          )
        }
      })
    }
  }

  const result: CustomComponentManifest = {
    customComponentSupportVersion: '2',
    components: {},
  }

  for (const [exportedName, extracted] of Object.entries(foundTransitiveCallExpressions)) {
    const manifestEntry: CustomComponentManifestEntry = {
      name: camelToSpaceTitle(exportedName),
      model: [],
      events: [],
      defaultHeight: 5,
      defaultWidth: 2,
    }

    for (const callExpression of extracted.retoolStateHooks) {
      const type = extractTypeUsedForUseRetoolState(callExpression)
      if (type === undefined) {
        // Invalid or unknown hook, skip for generating manifest
        continue
      }

      const [argument] = getArguments(callExpression, 1)

      if (!isLiteralExpressionKind(argument.kind)) {
        throw new NonLiteralParameterError(callExpression, argument)
      }

      if (argument.kind !== ts.SyntaxKind.ObjectLiteralExpression) {
        throw new LiteralTypeError(argument, 'object')
      }

      const mainArg = argument as ts.ObjectLiteralExpression
      const manifestModelField: CustomComponentManifestModelField = {
        name: getRequiredPropertyValue({
          entireExpression: callExpression,
          mainArg,
          key: 'name',
          expectedType: 'string'
        }),
        initialValue: getOptionalPropertyValue({
          entireExpression: callExpression,
          mainArg,
          key: 'initialValue',
          expectedType: 'unknown',
        }),
        inspector: getOptionalPropertyValue({
          entireExpression: callExpression,
          mainArg,
          key: 'inspector',
          expectedType: 'string',
        }),
        label: getOptionalPropertyValue({
          entireExpression: callExpression,
          mainArg,
          key: 'label',
          expectedType: 'string'
        }),
        enumDefinition: getOptionalPropertyValue({
          entireExpression: callExpression,
          mainArg,
          key: 'enumDefinition',
          expectedType: 'string[]',
        }),
        enumLabels: getOptionalPropertyValue({
          entireExpression: callExpression,
          mainArg,
          key: 'enumLabels',
          expectedType: 'stringValuedObject',
        }),
        description: getOptionalPropertyValue({
          entireExpression: callExpression,
          mainArg,
          key: 'description',
          expectedType: 'string'
        }), 
        type,
      }

      const existingField = manifestEntry.model.find((model) => {
        return model.name === manifestModelField.name
      })
      if (existingField) {
        if (manifestModelField.type !== existingField.type) {
          throw Error('State field defined twice with different types!')
        }
      } else {
        manifestEntry.model.push(manifestModelField)
      }
    }

    for (const callExpression of extracted.eventCallbacks) {
      const [argument] = getArguments(callExpression, 1)

      if (!isLiteralExpressionKind(argument.kind)) {
        throw new NonLiteralParameterError(callExpression, argument)
      }

      if (argument.kind !== ts.SyntaxKind.ObjectLiteralExpression) {
        throw new LiteralTypeError(argument, 'object')
      }

      const name = getOptionalPropertyValue({
        entireExpression: callExpression,
        mainArg: argument as ts.ObjectLiteralExpression,
        key: 'name',
        expectedType: 'string'
      })
      if (!name) {
      } else {
        // For some reason we count all callExpressions twice :shrug:. So here we avoid duplication
        if (!manifestEntry.events.includes(name)) {
          manifestEntry.events.push(name)
        }
      }
    }

    for (const callExpression of extracted.componentSettings) {
      const [argument] = getArguments(callExpression, 1)

      if (!isLiteralExpressionKind(argument.kind)) {
        throw new NonLiteralParameterError(callExpression, argument)
      }

      if (argument.kind !== ts.SyntaxKind.ObjectLiteralExpression) {
        throw new LiteralTypeError(argument, 'object')
      }


      const mainArg = argument as ts.ObjectLiteralExpression
      const defaultHeight = getOptionalPropertyValue({
        entireExpression: callExpression,
        mainArg,
        key: 'defaultHeight',
        expectedType: 'number',
      }) 
      const defaultWidth = getOptionalPropertyValue({
        entireExpression: callExpression,
        mainArg,
        key: 'defaultWidth',
        expectedType: 'number',
      })
      if (defaultHeight) {
        manifestEntry.defaultHeight = defaultHeight
      }
      if (defaultWidth) {
        manifestEntry.defaultWidth = defaultWidth
      }
    }

    result.components[exportedName] = manifestEntry
  }
  return result
}

export const generateManifest = () => {
  const { entryPoint } = getCustomComponentLibraryConfig()

  const manifest = analyzeFile(entryPoint, 'tsconfig.json', '.')

  const outputFile = 'retool-custom-component-manifest.json'

  return fs.writeFileSync(outputFile, JSON.stringify(manifest, null, 2))
}

const isModelType = (type: string): type is ModelType => {
  return MODEL_TYPES.includes(type as ModelType)
}

function extractTypeUsedForUseRetoolState(
  callExpression: ts.CallExpression,
): ModelType | undefined {
  const funcName = callExpression.expression.getText()
  const useStateFunctionName = funcName.split('.')[1]
  const splitCamelCase = useStateFunctionName.split(/(?=[A-Z])/)
  const type = splitCamelCase[splitCamelCase.length - 1].toLowerCase()
  if (isModelType(type)) {
    return type
  }

  return undefined
}

const isLiteralExpressionKind = (kind: ts.SyntaxKind): boolean => {
  return kind === ts.SyntaxKind.ObjectLiteralExpression
    || kind === ts.SyntaxKind.ArrayLiteralExpression
    || kind === ts.SyntaxKind.StringLiteral
    || kind === ts.SyntaxKind.FalseKeyword
    || kind === ts.SyntaxKind.TrueKeyword
    || kind === ts.SyntaxKind.PrefixUnaryExpression
    || kind === ts.SyntaxKind.FirstLiteralToken
}

function parseLiteralValue<TExpectedType extends LiteralType>(
  fullExpression: ts.CallExpression,
  value: ts.Expression,
  expectedType: TExpectedType,
): LiteralTypeValue<TExpectedType> {
  let parsedValue: unknown
  switch (value.kind) {
    case ts.SyntaxKind.ObjectLiteralExpression:
      const parsedObject = {};
      (value as ts.ObjectLiteralExpression).properties.forEach((p) => {
        if (ts.isPropertyAssignment(p)) {
          const key = stripQuotes(p.name.getText())
          if (key !== undefined) {
            parsedObject[key] = parseLiteralValue(fullExpression, p.initializer, 'unknown')
          }
        } else {
          throw new NonLiteralParameterError(fullExpression, value)
        }
      })
      parsedValue = parsedObject
      break
    case ts.SyntaxKind.ArrayLiteralExpression:
      const parsedArray = (value as ts.ArrayLiteralExpression).elements.map((e) => {
        return parseLiteralValue(fullExpression, e, 'unknown')
      })
      parsedValue = parsedArray
      break
    case ts.SyntaxKind.StringLiteral:
      parsedValue = stripQuotes(value.getText())
      break
    case ts.SyntaxKind.FalseKeyword:
      parsedValue = false
      break
    case ts.SyntaxKind.TrueKeyword:
      parsedValue = true
      break
    case ts.SyntaxKind.PrefixUnaryExpression:
      parsedValue = Number(value.getText())
      break
    case ts.SyntaxKind.FirstLiteralToken:
      parsedValue = Number(value.getText())
      break
    default:
      throw new NonLiteralParameterError(fullExpression, value)
  }

  if (!matchesLiteralValueType(parsedValue, expectedType)) {
    throw new LiteralTypeError(value, expectedType)
  }

  return parsedValue
}

function lineAndCharacter(errorNode: ts.Node) {
  const sourceFile = errorNode.getSourceFile()
  const lineAndCharacter = sourceFile.getLineAndCharacterOfPosition(errorNode.getStart())
  return {
    line: lineAndCharacter.line,
    character: lineAndCharacter.character,
  }
}

const getObjectLiteralPropertyInitializerValueNode = (
  objectLiteral: ts.ObjectLiteralExpression,
  key: string,
): ts.Expression | undefined => {
  const prop = objectLiteral.properties.find((p) => p.name?.getText() === key) as ts.PropertyAssignment | undefined
  return prop?.initializer
}

const getRequiredPropertyValue = <TExpectedType extends LiteralType>({
  entireExpression,
  mainArg,
  key,
  expectedType,
}: {
  entireExpression: ts.CallExpression,
  mainArg: ts.ObjectLiteralExpression,
  key: string,
  expectedType: TExpectedType
}): LiteralTypeValue<TExpectedType> => {
  const value = getObjectLiteralPropertyInitializerValueNode(mainArg, key)
  if (!value) {
    throw new RequiredPropertyError(mainArg, key)
  }
  return parseLiteralValue(entireExpression, value, expectedType)
}

const getOptionalPropertyValue = <TExpectedType extends LiteralType>({
  entireExpression,
  mainArg,
  key,
  expectedType,
}: {
  entireExpression: ts.CallExpression,
  mainArg: ts.ObjectLiteralExpression,
  key: string,
  expectedType: TExpectedType
}): LiteralTypeValue<TExpectedType> | undefined => {
  const value = getObjectLiteralPropertyInitializerValueNode(mainArg, key)
  if (!value) {
    return undefined
  }
  return parseLiteralValue(entireExpression, value, expectedType)
}

const matchesLiteralValueType = <TExpectedType extends LiteralType>(
  value: unknown,
  expected: TExpectedType,
): value is LiteralTypeValue<TExpectedType> => {
  switch (expected) {
    case 'string[]':
      return Array.isArray(value) && value.every(elem => typeof elem === 'string')
    case 'boolean':
      return typeof value === 'boolean'
    case 'number':
      return typeof value === 'number'
    case 'object':
      return value !== null && typeof value === 'object' && !Array.isArray(value)
    case 'stringValuedObject':
      return (
        value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.values(value).every((val) => typeof val === 'string')
      )
    case 'string':
      return typeof value === 'string'
    case 'unknown':
      return true
    default:
      throw new TypeError('Unexpected value type in matchesLiteralValueType()')
  }
}

function stripQuotes(input: string | undefined): string | undefined {
  // Check if the input string is enclosed in single or double quotes
  if (input === undefined) {
    return undefined
  }
  if ((input.startsWith("'") && input.endsWith("'")) || (input.startsWith('"') && input.endsWith('"'))) {
    // If so, remove the enclosing quotes
    return input.slice(1, -1)
  } else {
    // If not, return the input string as is
    return input
  }
}

const getArguments = (callExpression: ts.CallExpression, expectedCount: number): ts.NodeArray<ts.Expression> => {
  const actualCount = callExpression.arguments.length 
  if (actualCount !== expectedCount) {
    throw new ArgumentsCountError(callExpression.expression, expectedCount, actualCount)
  }
  return callExpression.arguments
}

function startsWithVowel(word: string): boolean {
  const firstLetter = word.charAt(0).toLowerCase()
  return ['a', 'e', 'i', 'o', 'u'].includes(firstLetter)
}

class EsBuildError extends Error {
  errors: EsBuildErrorDescription[]
  constructor(errors: EsBuildErrorDescription[]) {
    super(errors[0].text)
    this.errors = errors
  }
}

class NodeSpecificEsBuildError extends EsBuildError {
  constructor(problematicNode: ts.Node, errorText: string, suggestion = '') {
   const { line, character } = lineAndCharacter(problematicNode)
    const sourceFile = problematicNode.getSourceFile()
    const lineStarts = sourceFile.getLineStarts()
    const lineText = sourceFile.text.substring(lineStarts[line], lineStarts[line + 1])
    const errorMessage: EsBuildErrorDescription = {
      pluginName: 'generate-manifest',
      id: 'error',
      text: errorText,
      detail: null,
      notes: [],
      location: {
        file: problematicNode.getSourceFile().fileName,
        line: line + 1,
        column: character,
        length: problematicNode.getText().length,
        lineText,
        suggestion,
      },
    }
    super([errorMessage])
  }
}

class NonLiteralParameterError extends NodeSpecificEsBuildError {
  constructor(fullExpression: ts.CallExpression, problematicNode: ts.Node) {
    let problematicNodeType = ts.SyntaxKind[problematicNode.kind]
    switch (problematicNode.kind) {
      case ts.SyntaxKind.Identifier:
        problematicNodeType = 'identifier'
        break
      case ts.SyntaxKind.ArrowFunction:
      case ts.SyntaxKind.FunctionExpression:
        problematicNodeType = 'function'
        break
      case ts.SyntaxKind.BinaryExpression:
        problematicNodeType = 'expression'
      default:
        break
    }
    const errorText =
      `${fullExpression.expression.getText()} was called with a non literal parameter "${problematicNode.getText()}".` +
      ` "${problematicNode.getText()}" is not allowed as a parameter to ${fullExpression.expression.getText()} because it's` +
      ` ${
        startsWithVowel(problematicNodeType) ? 'an' : 'a'
      } ${problematicNodeType}, only literal values such as "example-string" or [1,2,3] are allowed.`
      const suggestion =
      'You must use a literal value, such as 12954 or false or { a: "2"}, you may not use a reference to a variable or expression here.'
     super(problematicNode, errorText, suggestion)
  }
}

class ArgumentsCountError extends NodeSpecificEsBuildError {
  constructor(expression: ts.LeftHandSideExpression, expectedCount: number, actualCount: number) {
    super(expression, `Expected ${expectedCount} arguments to ${expression.getText()} but got ${actualCount}`)
  }
}

class LiteralTypeError extends NodeSpecificEsBuildError {
  constructor(expression: ts.Expression, expectedType: LiteralType) {
    super(expression, `Literal expression ${expression.getText()} has invalid type, expected ${expectedType}`)
  }
}

class RequiredPropertyError extends NodeSpecificEsBuildError{
  constructor(expression: ts.Expression, key: string) {
    super(expression, `Arguments expression ${expression.getText()} missing required property '${key}'`,)
  }
}
